import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import TopNav from '../../components/layout/TopNav';
import { useVideoStore, useAuthStore, useUploadStore } from '../../store';
import api from '../../services/api';
import GradeBadge from '../../components/shared/GradeBadge';
import Tooltip from '../../components/shared/Tooltip';
import ScoringStatusChip from '../../components/shared/ScoringStatusChip';
import { UserRole } from '../../types';
import type { Video, User, Grade, ScoringStatus } from '../../types';
import { useProcessingPoller } from '../../hooks/useProcessingPoller';

const VideosPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userRole = useAuthStore((s) => s.user?.role);
  const canUpload = userRole === UserRole.SALES_REP;
  const isTenantAdmin = userRole === UserRole.TENANT_ADMIN;
  const { videos, setVideos, totalVideos } = useVideoStore();
  const dismissByVideoId = useUploadStore((s) => s.dismissByVideoId);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filterGrade, setFilterGrade] = useState<string>(searchParams.get('grade') ?? 'ALL');
  const [filterStatus, setFilterStatus] = useState<string>(searchParams.get('status') ?? 'ALL');
  const [filterMember, setFilterMember] = useState<string>('ALL');
  const [filterHasFlags, setFilterHasFlags] = useState<boolean>(searchParams.get('hasFlags') === 'true');
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
  const [streamUrls, setStreamUrls] = useState<Record<string, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [rescoringIds, setRescoringIds] = useState<Set<string>>(new Set());
  const perPage = 15;

  // Fetch team members for tenant admin filter
  useEffect(() => {
    if (!isTenantAdmin) return;
    api.get('/users', { params: { limit: 1000 } }).then((res) => {
      const users: User[] = res.data.data ?? [];
      setTeamMembers(users.filter((u) => u.role === UserRole.SALES_REP));
    }).catch(() => { /* silently handle */ });
  }, [isTenantAdmin]);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page, perPage };
      if (filterGrade !== 'ALL') params.grade = filterGrade;
      if (filterStatus !== 'ALL') params.scoringStatus = filterStatus;
      if (filterMember !== 'ALL') params.uploadedBy = filterMember;
      if (filterHasFlags) params.hasFlags = true;
      const res = await api.get('/videos', { params });
      const videoList = res.data.data as Video[];
      setVideos(videoList, res.data.meta?.total ?? 0);

      // Fetch presigned thumbnail URLs first (static JPEG, 24 h TTL)
      const ids = videoList.map((v) => v.id);
      if (ids.length > 0) {
        const thumbRes = await api.post('/videos/thumbnail-urls', { videoIds: ids });
        const thumbMap: Record<string, string> = thumbRes.data.data ?? {};
        setThumbnailUrls(thumbMap);

        // For videos without a generated thumbnail, fall back to the stream URL
        // so the browser can seek to frame 0.5 s as before
        const missingIds = videoList
          .filter((v: Video) => v.storagePath && !thumbMap[v.id])
          .map((v: Video) => v.id);
        if (missingIds.length > 0) {
          const streamRes = await api.post('/videos/stream-urls', { videoIds: missingIds });
          setStreamUrls(streamRes.data.data ?? {});
        } else {
          setStreamUrls({});
        }
      }
    } catch {
      // silently handle
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filterGrade, filterStatus, filterMember, filterHasFlags, setVideos]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Auto-refresh when any PROCESSING/PENDING video completes in the background
  const { processingStages } = useProcessingPoller(videos, fetchVideos);

  const handleRescore = async (videoId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRescoringIds((prev) => new Set(prev).add(videoId));
    try {
      await api.post(`/videos/${videoId}/rescore`, {});
      toast.success('Video queued for rescoring.');
      await fetchVideos();
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Failed to queue rescore.';
      toast.error(message);
    } finally {
      setRescoringIds((prev) => {
        const next = new Set(prev);
        next.delete(videoId);
        return next;
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/videos/${deleteTarget.id}`);
      dismissByVideoId(deleteTarget.id);
      toast.success('Video deleted.');
      setDeleteTarget(null);
      await fetchVideos();
    } catch {
      toast.error('Failed to delete video.');
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(totalVideos / perPage);

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 transition-colors duration-300 font-sans flex flex-col">
      <TopNav />
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">Video Library</h1>
            <p className="text-slate-500 dark:text-slate-400 font-medium text-base mt-2">
              <span className="text-slate-900 dark:text-white font-bold">{totalVideos}</span> videos in library
            </p>
          </div>
          {canUpload && (
            <button
              onClick={() => navigate('/upload')}
              className="h-11 px-8 rounded-xl font-bold bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/10 hover:shadow-xl hover:shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-[20px]">cloud_upload</span>
              Upload Video
            </button>
          )}
        </div>

        {/* Filters & Grid Container */}
        <div>
          <div className="flex flex-wrap gap-4 mb-8">
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Grade Level</label>
              <select
                value={filterGrade}
                onChange={(e) => { setFilterGrade(e.target.value); setPage(1); }}
                className="h-10 px-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm font-bold text-slate-600 dark:text-slate-400 outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 transition-all cursor-pointer appearance-none pr-10 min-w-[140px]"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
              >
                <option value="ALL">All Grades</option>
                {['A', 'B', 'C', 'D', 'F'].map((g) => (
                  <option key={g} value={g}>Grade {g}</option>
                ))}
              </select>
            </div>
            
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Process Status</label>
              <select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                className="h-10 px-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm font-bold text-slate-600 dark:text-slate-400 outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 transition-all cursor-pointer appearance-none pr-10 min-w-[140px]"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
              >
                <option value="ALL">All Statuses</option>
                {['PENDING', 'PROCESSING', 'SCORED', 'FLAGGED', 'FAILED'].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Compliance</label>
              <button
                onClick={() => { setFilterHasFlags((p) => !p); setPage(1); }}
                className={`h-10 px-4 rounded-xl text-sm font-bold transition-all border ${filterHasFlags ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800 text-red-500 dark:text-red-400' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:border-primary/40'}`}
              >
                {filterHasFlags ? 'Flagged Only ✕' : 'All Videos'}
              </button>
            </div>

            {isTenantAdmin && teamMembers.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Team Member</label>
                <select
                  value={filterMember}
                  onChange={(e) => { setFilterMember(e.target.value); setPage(1); }}
                  className="h-10 px-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm font-bold text-slate-600 dark:text-slate-400 outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 transition-all cursor-pointer appearance-none pr-10 min-w-[140px]"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                >
                  <option value="ALL">All Team Members</option>
                  {teamMembers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {`${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || m.email}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

        {/* Video Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full shadow-[0_0_10px_rgba(var(--primary-rgb),0.2)]" />
            <p className="text-sm font-bold text-slate-400 dark:text-slate-500 tracking-tight">Syncing library...</p>
          </div>
        ) : videos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 bg-white dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800 border-dashed rounded-3xl text-slate-400 dark:text-slate-600">
            <div className="w-16 h-16 rounded-2xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center mb-6 text-slate-300 dark:text-slate-700">
              <span className="material-symbols-outlined text-5xl">videocam_off</span>
            </div>
            <p className="font-bold text-lg text-slate-900 dark:text-white tracking-tight">No videos found</p>
            <p className="text-sm font-medium mt-1">Try adjusting your filters or upload a new video.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {videos.map((video: Video) => {
              const isClickable = video.scoringStatus !== 'PENDING' && video.scoringStatus !== 'PROCESSING';
              return (
              <div
                key={video.id}
                onClick={() => isClickable && navigate(`/videos/${video.id}`)}
                className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden shadow-sm dark:shadow-none transition-all group flex flex-col h-full ${isClickable ? 'cursor-pointer hover:shadow-xl hover:shadow-primary/5 dark:hover:border-primary/30' : 'cursor-default opacity-60'}`}
              >
                {/* Video thumbnail */}
                <div className="aspect-video bg-slate-100 dark:bg-slate-800 relative overflow-hidden shrink-0">
                  {thumbnailUrls[video.id] ? (
                    <img
                      src={thumbnailUrls[video.id]}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : streamUrls[video.id] ? (
                    <video
                      src={`${streamUrls[video.id]}#t=0.5`}
                      muted
                      preload="metadata"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300 dark:text-slate-700">
                      <span className="material-symbols-outlined text-5xl">videocam</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-white/30 backdrop-blur-md flex items-center justify-center text-white opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all shadow-xl">
                      <span className="material-symbols-outlined text-[32px] fill-current">play_arrow</span>
                    </div>
                  </div>
                  {video.durationSeconds && (
                    <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/60 backdrop-blur-md text-white text-[10px] font-black tracking-widest rounded-lg">
                      {Math.floor(video.durationSeconds / 60)}:{String(video.durationSeconds % 60).padStart(2, '0')}
                    </div>
                  )}
                </div>

                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex flex-col min-w-0">
                      <h3 className="text-base font-bold text-slate-900 dark:text-white whitespace-normal break-words leading-snug group-hover:text-primary transition-colors">
                        {video.title}
                      </h3>
                      <span className="text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-widest mt-1">
                        Uploaded {new Date(video.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {canUpload && (video.scoringStatus === 'PENDING' || video.scoringStatus === 'FLAGGED') && (
                        <Tooltip content="Rescore video">
                          <button
                            onClick={(e) => handleRescore(video.id, e)}
                            disabled={rescoringIds.has(video.id)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all text-amber-500 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {rescoringIds.has(video.id) ? (
                              <div className="w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent animate-spin rounded-full" />
                            ) : (
                              <span className="material-symbols-outlined text-[18px]">refresh</span>
                            )}
                          </button>
                        </Tooltip>
                      )}
                      <Tooltip content={isClickable ? 'Delete video' : 'Cannot delete while processing'}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isClickable) setDeleteTarget({ id: video.id, title: video.title });
                          }}
                          disabled={!isClickable}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 ${isClickable ? 'text-slate-400 dark:text-slate-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 dark:hover:text-red-400' : 'text-slate-200 dark:text-slate-700 cursor-not-allowed'}`}
                        >
                          <span className="material-symbols-outlined text-[18px]">delete_sweep</span>
                        </button>
                      </Tooltip>
                    </div>
                  </div>

                  <div className="mt-auto pt-6 border-t border-slate-50 dark:border-white/5 flex items-center justify-between">
                    {video.videoScore ? (
                      <div className="flex items-center gap-3">
                        <GradeBadge grade={video.videoScore.grade as Grade} size="sm" />
                        <div className="flex flex-col">
                           <span className="text-[14px] font-black text-slate-900 dark:text-white">{video.videoScore.compositeScore}</span>
                           <span className="text-[9px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-tighter -mt-1">VQS Score</span>
                        </div>
                      </div>
                    ) : (
                      <ScoringStatusChip status={video.scoringStatus as ScoringStatus} label={processingStages[video.id]} />
                    )}
                    
                    <div className="flex -space-x-2">
                       {/* Placeholder for tags or avatars if needed */}
                       <div className="w-6 h-6 rounded-full bg-slate-50 dark:bg-slate-800 border-2 border-white dark:border-slate-900 flex items-center justify-center">
                          <span className="material-symbols-outlined text-[12px] text-slate-300 dark:text-slate-600">person</span>
                       </div>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-16 p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-fit mx-auto shadow-sm dark:shadow-none">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-primary dark:hover:text-primary hover:bg-primary/5 dark:hover:bg-primary/10 disabled:opacity-20 transition-all cursor-pointer"
            >
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <div className="px-4 text-sm font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest leading-none flex items-center">
              <span className="text-slate-900 dark:text-white">{page}</span> <span className="mx-2 text-slate-200 dark:text-slate-800">/</span> {totalPages}
            </div>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-primary dark:hover:text-primary hover:bg-primary/5 dark:hover:bg-primary/10 disabled:opacity-20 transition-all cursor-pointer"
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        )}
      </div>
    </main>

      {/* Delete Confirmation Modal - Moved to page root */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 p-8 space-y-8 text-center max-w-md w-full rounded-[2.5rem] shadow-2xl animate-in zoom-in-95 duration-200 border border-white/5 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-red-500" />
            <div className="mx-auto p-4 bg-red-50 dark:bg-red-900/20 rounded-full w-fit">
              <span className="material-symbols-outlined text-[32px] text-red-500 dark:text-red-400 block">delete_forever</span>
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white uppercase tracking-tight">Delete Permanently?</h3>
              <p className="text-slate-500 dark:text-slate-400 font-medium text-sm leading-relaxed">
                This will destroy <span className="text-slate-900 dark:text-white font-bold">{deleteTarget.title}</span> along with all AI analysis and scoring data.
              </p>
              <p className="text-[10px] font-black text-red-400 dark:text-red-500/80 uppercase tracking-widest">This action cannot be undone</p>
            </div>
            <div className="flex items-center gap-3 pt-4">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 h-12 rounded-xl font-bold border border-slate-100 dark:border-slate-800 text-slate-400 dark:text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all cursor-pointer text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 h-12 rounded-xl font-bold bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-200 dark:shadow-none transition-all cursor-pointer text-sm"
              >
                {deleting ? 'Destroying...' : 'Delete Video'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideosPage;
